import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ReservesDto,
  DrawnUserJackpotDto,
  JackpotControllerGetActiveJackpotsApiResponse,
  JackpotControllerGetUserWinsFromJackpotsApiResponse,
} from '../../../app/store/api/jackpotApi';
import { JackpotSlug, JackpotTabEnum } from '@entities/jackpot';

interface TooltipParams {
  drawAmount: string;
  leftDirection: boolean;
  jackpotSlug: JackpotSlug | null;
  position: { top: number; left: number };
}

export interface JackpotSliceState {
  jackpotTab: JackpotTabEnum;
  activeJackpots: JackpotControllerGetActiveJackpotsApiResponse;
  userJackpots: DrawnUserJackpotDto[];
  userJackpotsCount: number;
  userJackpotsOffset: number;
  loadingUserJackpots: boolean;
  fetchingUserJackpots: boolean;
  userWinsData?: JackpotControllerGetUserWinsFromJackpotsApiResponse;
  drawJackpot: string[];
  reservesJackpots?: ReservesDto;
  tooltipParams?: TooltipParams;
  lastAnimatedJackpotBalance: number | null;
  platinumNextDrawMs?: number;
}

const initialState: JackpotSliceState = {
  jackpotTab: JackpotTabEnum.MyJackpots,
  activeJackpots: [],
  userJackpots: [],
  userJackpotsCount: 0,
  userJackpotsOffset: 0,
  loadingUserJackpots: true,
  fetchingUserJackpots: true,
  userWinsData: undefined,
  drawJackpot: [],
  reservesJackpots: undefined,
  tooltipParams: undefined,
  lastAnimatedJackpotBalance: 0,
};

const userJackpotSlice = createSlice({
  name: 'jackpot',
  initialState,
  reducers: {
    setJackpotTab: (state, { payload }: PayloadAction<JackpotTabEnum>) => {
      state.jackpotTab = payload;
    },
    setActiveJackpots: (
      state,
      {
        payload,
      }: PayloadAction<JackpotControllerGetActiveJackpotsApiResponse | undefined>,
    ) => {
      state.activeJackpots = payload ?? [];
    },
    setUserJackpots: (state, { payload }: PayloadAction<DrawnUserJackpotDto[]>) => {
      state.userJackpots = payload;
    },
    setReservesJackpots: (state, { payload }: PayloadAction<ReservesDto>) => {
      state.reservesJackpots = payload;
    },
    setLoadingUserJackpots: (state, { payload }: PayloadAction<boolean>) => {
      state.loadingUserJackpots = payload;
    },
    setFetchingUserJackpots: (state, { payload }: PayloadAction<boolean>) => {
      state.fetchingUserJackpots = payload;
    },
    setUserWinsData: (
      state,
      {
        payload,
      }: PayloadAction<JackpotControllerGetUserWinsFromJackpotsApiResponse | undefined>,
    ) => {
      state.userWinsData = payload;
    },
    setUserJackpotsCount: (state, { payload }: PayloadAction<number>) => {
      state.userJackpotsCount = payload;
    },
    resetUserJakpotsOffset: (state) => {
      state.userJackpotsOffset = 0;
    },
    incrementUserJackpotsOffset: (state) => {
      state.userJackpotsOffset += 1;
    },
    setDrawJackpot: (state, { payload }: PayloadAction<string[]>) => {
      state.drawJackpot = payload;
    },
    setHideTooltip: (state) => {
      state.tooltipParams = undefined;
    },
    setTooltipParams: (state, { payload }: PayloadAction<TooltipParams | undefined>) => {
      state.tooltipParams = payload;
    },

    setLastAnimatedJackpotBalance: (state, { payload }: PayloadAction<number>) => {
      state.lastAnimatedJackpotBalance = payload;
    },
    setPlatinumNextDrawMs: (state, { payload }: PayloadAction<number | undefined>) => {
      state.platinumNextDrawMs = payload;
    },
  },
});

export const {
  setJackpotTab,
  setHideTooltip,
  setDrawJackpot,
  setUserWinsData,
  setUserJackpots,
  setTooltipParams,
  setActiveJackpots,
  setReservesJackpots,
  setUserJackpotsCount,
  setLoadingUserJackpots,
  resetUserJakpotsOffset,
  setFetchingUserJackpots,
  incrementUserJackpotsOffset,
  setLastAnimatedJackpotBalance,
  setPlatinumNextDrawMs,
} = userJackpotSlice.actions;

export default userJackpotSlice.reducer;
